<script lang="ts">
  import type { Snippet } from 'svelte'
  import type { Area } from '../../../shared/contract'
  import Lamp from './Lamp.svelte'
  import RunGroups from './RunGroups.svelte'
  import RunItem from './RunItem.svelte'
  import { count, filtering, liftRows, matches, sortRuns, type Filter } from '../tally'
  import type { Opening } from '../when'

  /**
   * The desktop directory: every run named, in balanced columns by
   * area. A filter fades the runs that don't match rather than removing them.
   * Focused on one area: its lifts, then its runs under grade headings, larger.
   * When a short or narrow window can't show every run, the columns scroll.
   */
  let {
    areas,
    filter,
    anyOpen,
    opens,
    focus,
    onall,
    dock,
  }: {
    areas: readonly Area[]
    filter: Filter
    anyOpen: boolean
    /** Opening day, while it is still ahead. */
    opens: Opening | null
    focus: Area | null
    onall: () => void
    dock: Snippet
  } = $props()

  const f = $derived(filtering(filter))
  const scope = $derived(focus ? focus.runs : areas.flatMap((a) => a.runs))
  const c = $derived(count(scope))
  const groomed = $derived(scope.filter((r) => r.groomed).length)
  const matched = $derived(scope.filter((r) => matches(r, filter)).length)
</script>

<section id="board" class="board" class:focus={!!focus} aria-label={focus ? `${focus.name} runs` : 'All runs'}>
  <div class="posts" aria-hidden="true"></div>
  <div class="bh">
    <div class="ttl">
      <span class="mono">{focus ? focus.name : 'All runs'}</span>
      {#if focus || f}
      <span class="cnt">
        {#if f}
          <b>{matched}</b> of {c.total} match
        {:else if anyOpen}
          <b>{c.open}</b> of {c.total} open · <b>{groomed}</b> groomed
        {:else}
          {c.total} runs{opens ? ` · opens ${opens.when}` : ''}
        {/if}
      </span>
      {/if}
    </div>
    {#if focus}<button class="back" onclick={onall}>All areas</button>{/if}
    {@render dock()}
  </div>
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <div class="cols" tabindex="0" role="region" aria-label="Runs">
    <div class="flow">
    {#if focus}
      <ul class="fl" aria-label="Lifts">
        {#each liftRows(focus.lifts) as lift}
          <li class="lift" class:on={lift.turning} aria-label={lift.label} title={lift.label}>
            <Lamp on={lift.turning} /><span>{lift.name}</span>
            {#if lift.status}<span class="hold mono">{lift.status}</span>{/if}
          </li>
        {:else}
          <li class="lift"><span class="mono">No lifts</span></li>
        {/each}
      </ul>
      <RunGroups runs={focus.runs} {filter} {anyOpen} fade />
    {:else}
      {#each areas as area}
        {@const ac = count(area.runs)}
        <div class="area">
          <h3 class="mono">
            {area.name}
            <span>
              {f
                ? area.runs.filter((r) => matches(r, filter)).length
                : anyOpen
                  ? `${ac.open}/${ac.total}`
                  : ac.total}
            </span>
          </h3>
          <ul>
            {#each sortRuns(area.runs) as run}
              <RunItem {run} ghost={f && !matches(run, filter)} />
            {/each}
          </ul>
        </div>
      {/each}
    {/if}
    </div>
  </div>
</section>

<style>
  .board {
    grid-column: 1;
    grid-row: 3;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: relative;
    background: #f3f6f5;
    border: 2px solid #ffffff;
    border-image: none;
    border-radius: 2px;
    padding: 7px 10px 0;
    box-shadow: 0 9px 25px #142c2b30, 0 0 0 1px #90a3a9, inset 0 0 0 1px #c8d2d5;
    container: board / inline-size;
    isolation: isolate;
  }
  .board::before { content: ''; pointer-events: none; position: absolute; left: -2px; right: -2px; top: -5px; height: 4px; border-radius: 65% 30% 2px 1px; background: #f5f8ef; }
  /* The enamel backing covers the posts while their square shoulders stay exposed. */
  .board::after { content: ''; pointer-events: none; position: absolute; inset: 0; background: #f3f6f5; z-index: -1; }
  .posts { position: absolute; inset: -8px -12px -14px; z-index: -2; pointer-events: none; }
  .posts::before, .posts::after { content: ''; position: absolute; top: 0; bottom: 0; width: 48px; background: var(--timber); border: 1px solid #8d795c; border-radius: 1px; box-shadow: inset 2px 2px 0 #f3e1bd80, inset -7px 0 0 #67523b40, 3px 2px 5px #15282035; }
  .posts::before { left: 0; }
  .posts::after { right: 0; }
  /* A narrow board puts the filter on its own row under the title. */
  .bh {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    flex: none;
    padding-bottom: 7px;
    border-bottom: 1px solid var(--line);
  }
  .ttl {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }
  .ttl .mono {
    color: var(--ink);
    font-size: 13px;
    font-weight: 750;
    letter-spacing: .1em;
  }
  .cnt {
    color: var(--ink-2);
    font-size: 12px;
    white-space: nowrap;
  }
  .cnt b {
    color: var(--ink);
    font-weight: 400;
  }
  .back {
    color: var(--ink-2);
    font-size: 13px;
    height: 44px;
    padding: 0 12px;
    border-radius: 2px;
    box-shadow: inset 0 0 0 1px var(--line);
    margin-left: 4px;
    white-space: nowrap;
  }
  .back:hover {
    color: var(--ink);
  }
  .cols {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    margin: 0 -10px;
    padding: 7px 10px 8px;
    border-radius: 0;
  }
  .cols:focus-visible {
    outline-offset: -2px;
  }
  .flow {
    columns: 190px 6;
    column-gap: 8px;
    column-fill: balance;
  }
  .area {
    margin-bottom: 6px;
  }
  h3 {
    letter-spacing: 0.08em;
    padding: 3px 5px;
    margin-bottom: 2px;
    background: #bc263c;
    border: 1px solid #ffffff;
    break-after: avoid;
    color: #ffffff;
    text-transform: none;
    font-size: 12px;
    font-weight: 750;
    display: flex;
    gap: 8px;
    align-items: baseline;
  }
  h3 span {
    color: #ffffff;
    margin-left: auto;
  }
  .area:first-child h3 {
    padding-top: 3px;
  }
  .focus .flow {
    columns: 240px 4;
  }
  .fl {
    display: flex;
    flex-wrap: wrap;
    gap: 0 15px;
    padding: 0 0 3px;
    margin-bottom: 2px;
    break-after: avoid;
    column-span: all;
    border-bottom: 1px solid var(--line);
  }
  .lift {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--ink-3);
    font-size: 12px;
    font-weight: 600;
    height: 18px;
  }
  .lift.on {
    color: var(--ink);
  }
  .hold {
    color: var(--ink-2);
    letter-spacing: 0.08em;
  }
</style>
